import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Checkbox, MenuItem, Select, FormControl, Typography } from '@mui/material';
import styles from './FormListsTable.module.css'; // Reusing FormListsTable styles
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useMultiAnswerField } from '../../contexts/MultiAnswerFieldContext';
import { useFieldMetadata } from '../../contexts/FieldMetadataContext';
import { useDropdownDependencies } from '../../contexts/DropdownDependenciesContext';
import { useFormContext } from '../../contexts/FormContext';
import { useFormListsDraftSync } from '../../contexts/FormListsDraftSyncContext';

const DependencyManager = ({
  optionId,
  optionsSetUFI,
  dependencies: initialDependencies = [],
  onClose,
  dropdownOptionsOverride = null,
}) => {
  const DEBUG_DEPENDENCY_MANAGER = Boolean(
    typeof window !== 'undefined' &&
      window.__DEBUG_DEPENDENCY_MANAGER__ === true
  );

  const debugLog = useCallback((...args) => {
    if (!DEBUG_DEPENDENCY_MANAGER) return;
    console.log(...args);
  }, [DEBUG_DEPENDENCY_MANAGER]);

  /**
   * Selected dependent-field initialization guard
   * ---------------------------------------------------------------------------
   * DependencyManager should resolve the best dependent field when the panel
   * opens for a trigger option.
   *
   * It should NOT re-run that full initialization after every checkbox change.
   *
   * WHY
   *  • Checkbox changes update DropdownDependenciesContext.
   *  • Context updates change dependencyFlags / authoritative dependencies.
   *  • Re-running getFilteredOptions(...) after every checkbox creates visible
   *    lag and can cause the selected dependent field to jump.
   *
   * ENTERPRISE RULE
   *  • Initialize selectedFieldUFI once per trigger option identity.
   *  • Let checkbox state update locally and persist immediately.
   *  • Do not reselect the dependent field unless the user opens a different
   *    trigger option or the user explicitly changes the dropdown.
   */
  const selectedFieldInitializationKeyRef = useRef('');

  /**
   * Trigger option identity
   * ---------------------------------------------------------------------------
   * The trigger option is owned by the parent dependency-icon click.
   *
   * IMPORTANT
   *  • Do NOT store this in independent local state.
   *  • The side panel can remain mounted while the user clicks a different
   *    dependency icon.
   *  • If we keep a stale selectedOptionId in state, checkbox writes can be
   *    saved against the previously selected option instead of the current one.
   *
   * This value is the canonical optionId for dependency writes in this editor.
   */
  const triggerOptionId = optionId || '';

  const [selectedFieldUFI, setSelectedFieldUFI] = useState(optionsSetUFI || ''); // Track selected field UFI
  const [dependencies, setDependencies] = useState(initialDependencies);
  const [optionLabel, setOptionLabel] = useState(''); // Store the label associated with the optionId

  /**
   * Latest dependency selection ref
   * ---------------------------------------------------------------------------
   * Keeps checkbox updates synchronous and safe without calling context setters
   * from inside a React state updater.
   *
   * WHY
   *  • setDependencies((prev) => ...) must remain pure.
   *  • Calling updateDependenciesForOption(...) inside that updater can update
   *    DropdownDependenciesProvider while DependencyManager is processing its
   *    own local update.
   *  • That is the source of the maximum-update-depth loop on existing forms
   *    with dependency state.
   */
  const latestDependenciesRef = useRef(
    Array.isArray(initialDependencies) ? initialDependencies : []
  );

  // Access data from contexts
  const { dropdownOptions: contextDropdownOptions } = useMultiAnswerField(); // Dropdown options from MultiAnswerFieldContext
  
  /**
   * Effective dropdown options
   * ---------------------------------------------------------------------------
   * V2 Form Lists can pass an immediate draft override so DependencyManager
   * reflects spreadsheet-local edits before background autosave/context writes
   * have completed.
   *
   * Existing Form Lists / Field Settings behavior is preserved:
   *  • if no override is passed, read from MultiAnswerFieldContext as before.
   */
  const dropdownOptions =
    dropdownOptionsOverride &&
    typeof dropdownOptionsOverride === 'object'
      ? dropdownOptionsOverride
      : contextDropdownOptions;

  const { fieldsMetadata } = useFieldMetadata(); // Field metadata for labels from FieldMetadataContext
  const { getDependenciesForOption, updateDependenciesForOption, dependencyFlags, getFilteredOptions, loading } = useDropdownDependencies();
  const { formId } = useFormContext() || {};
  const { markFormListsUnsavedWorkingCopy } = useFormListsDraftSync() || {};

  /**
   * Authoritative dependencies for the current trigger option
   * ---------------------------------------------------------------------------
   * DropdownDependenciesContext remains the source of truth for persisted
   * dependency rules.
   *
   * Local `dependencies` state is only the immediate checkbox UI state.
   * It should sync from context when the trigger option/context changes, but
   * it should not be overwritten simply because the user clicked a checkbox
   * before React context has completed its update cycle.
   */
  const authoritativeDependencies = useMemo(() => {
    if (!optionsSetUFI || !triggerOptionId) {
      return [];
    }

    if (typeof getDependenciesForOption !== 'function') {
      return [];
    }

    return getDependenciesForOption(optionsSetUFI, triggerOptionId) || [];
  }, [
    getDependenciesForOption,
    optionsSetUFI,
    triggerOptionId,
  ]);

  const authoritativeDependenciesSignature = useMemo(() => (
    JSON.stringify(authoritativeDependencies || [])
  ), [
    authoritativeDependencies,
  ]);

  // Memoized dropdown field labels, optimized to reduce re-calculations
  const dropdownFieldLabels = useMemo(() => {
    debugLog('[DependencyManager: dropdownFieldLabels] Calculating dropdownFieldLabels...');

    return Object.values(fieldsMetadata)
      .filter(field =>
        (field.type === 'single-select' || field.type === 'multi-select') &&
        field.optionsSetUFI &&
        field.optionsSetUFI !== optionsSetUFI
      )
      .map(field => ({
        optionsSetUFI: field.optionsSetUFI,
        label: field.label || 'Unnamed Field'
      }));
  }, [
    fieldsMetadata,
    optionsSetUFI,
    debugLog,
  ]);

  const dropdownFieldLabelsSignature = useMemo(() => (
    dropdownFieldLabels
      .map((field) => `${field.optionsSetUFI}:${field.label}`)
      .join('|')
  ), [
    dropdownFieldLabels,
  ]);

  // Memoize availableOptions to avoid recalculating unless selectedFieldUFI changes
  const availableOptions = useMemo(() => {
    const options = dropdownOptions[selectedFieldUFI] || [];

    debugLog(
      `[DependencyManager] Loaded options for selectedFieldUFI ${selectedFieldUFI}:`,
      options
    );

    return options;
  }, [
    selectedFieldUFI,
    dropdownOptions,
    debugLog,
  ]);

  // Set optionLabel on mount or when optionId changes
  useEffect(() => {
    const initialLabel =
      dropdownOptions[optionsSetUFI]?.find(
        (option) => option.optionId === triggerOptionId
      )?.label || `Option ${triggerOptionId}`;

    setOptionLabel(initialLabel);

    debugLog(
      `[DependencyManager: useEffect] Set initial label for optionId ${triggerOptionId}:`,
      initialLabel
    );
  }, [
    triggerOptionId,
    optionsSetUFI,
    dropdownOptions,
    debugLog,
  ]);

  // Initialize dependencies when the trigger option or authoritative context changes.
  useEffect(() => {
    const nextAuthoritativeDependencies = Array.isArray(authoritativeDependencies)
      ? authoritativeDependencies
      : [];

    setDependencies((prevDependencies) => {
      const prevSignature = JSON.stringify(prevDependencies || []);

      if (prevSignature === authoritativeDependenciesSignature) {
        latestDependenciesRef.current = prevDependencies;
        return prevDependencies;
      }

      debugLog(
        '[DependencyManager: useEffect] Initialized dependencies:',
        nextAuthoritativeDependencies
      );

      latestDependenciesRef.current = nextAuthoritativeDependencies;
      return nextAuthoritativeDependencies;
    });
  }, [
    authoritativeDependencies,
    authoritativeDependenciesSignature,
    triggerOptionId,
    optionsSetUFI,
    debugLog,
  ]);

  useEffect(() => {
    if (loading) return;
    if (!triggerOptionId || !optionsSetUFI) return;

    const initializationKey = [
      optionsSetUFI,
      triggerOptionId,
      dropdownFieldLabelsSignature,
    ].join('::');

    if (selectedFieldInitializationKeyRef.current === initializationKey) {
      return;
    }

    selectedFieldInitializationKeyRef.current = initializationKey;

    debugLog(
      '[DependencyManager] Initializing selectedFieldUFI for optionId:',
      triggerOptionId
    );

    const filteredOptionsMap =
      typeof getFilteredOptions === 'function'
        ? getFilteredOptions(optionsSetUFI, triggerOptionId)
        : {};

    debugLog(
      `[DependencyManager] Filtered options map for dependencies based on optionId ${triggerOptionId}:`,
      filteredOptionsMap
    );

    const dependentFieldUFI = Object.keys(filteredOptionsMap || {}).find((ufi) =>
      filteredOptionsMap[ufi] && filteredOptionsMap[ufi].length > 0
    );

    if (dependentFieldUFI) {
      setSelectedFieldUFI((prev) => (
        prev === dependentFieldUFI ? prev : dependentFieldUFI
      ));

      debugLog(
        `[DependencyManager] Setting selectedFieldUFI to dependent field with existing dependencies: ${dependentFieldUFI}`
      );
      return;
    }

    const nextFieldUFI = dropdownFieldLabels[0]?.optionsSetUFI || '';

    setSelectedFieldUFI((prev) => (
      prev === nextFieldUFI ? prev : nextFieldUFI
    ));

    debugLog(
      '[DependencyManager] No dependencies found. Setting selectedFieldUFI to the next available field:',
      nextFieldUFI
    );
  }, [
    loading,
    triggerOptionId,
    optionsSetUFI,
    dropdownFieldLabelsSignature,
    dropdownFieldLabels,
    getFilteredOptions,
    debugLog,
  ]);


  // Check if there are enough fields to set up dependencies
  const hasInsufficientFields = useMemo(() => {
    const insufficient = dropdownFieldLabels.length < 1;
    if (insufficient) {
      console.log('[DependencyManager] Insufficient fields to set up dependencies');
    }
    return insufficient;
  }, [dropdownFieldLabels]);

  // Handle select field change
  const handleSelectChange = useCallback((event) => {
    const newSelectedFieldUFI = event.target.value;

    // Restrict dependency management to a single field if dependencies are already set
//    if (dependencies.length > 0 && selectedFieldUFI !== newSelectedFieldUFI) {
//      console.warn('[DependencyManager] Dependencies already set for another field; cannot change selection.');
//      return;
//    }
    setSelectedFieldUFI(newSelectedFieldUFI);
    debugLog(`[DependencyManager] Updated selectedFieldUFI to: ${newSelectedFieldUFI}`);
  }, [
    debugLog,
  ]);

  // Handle checkbox change to manage dependencies, with deferred saving until onClose
  const handleCheckboxChange = useCallback((dependentOptionId, checked) => {
    debugLog(`[DependencyManager] Checkbox changed for option ${dependentOptionId}: ${checked}`);

    if (!optionsSetUFI || !triggerOptionId) {
      console.error('[DependencyManager] Cannot update dependency because trigger option identity is missing.', {
        optionsSetUFI,
        triggerOptionId,
        dependentOptionId,
        checked,
      });
      return;
    }

    const safeCurrentDependencies = Array.isArray(latestDependenciesRef.current)
      ? latestDependenciesRef.current
      : [];

    const alreadySelected =
      safeCurrentDependencies.includes(dependentOptionId);

    const updatedDependencies = checked
      ? (
          alreadySelected
            ? safeCurrentDependencies
            : [...safeCurrentDependencies, dependentOptionId]
        )
      : safeCurrentDependencies.filter((dep) => dep !== dependentOptionId);

    if (
      JSON.stringify(updatedDependencies) ===
      JSON.stringify(safeCurrentDependencies)
    ) {
      return;
    }

    /**
     * Dependency write boundary
     * -------------------------------------------------------------------------
     * Keep local UI state and context persistence as two separate updates.
     *
     * WHY
     *  • setDependencies(...) must remain a pure local state update.
     *  • Calling updateDependenciesForOption(...) from inside the
     *    setDependencies updater causes React to update
     *    DropdownDependenciesProvider while DependencyManager is processing its
     *    own local update.
     *  • That creates the "Cannot update a component while rendering a
     *    different component" warning and can trigger a maximum update-depth
     *    freeze.
     *
     * ENTERPRISE RULE
     *  • Compute the next dependency list once.
     *  • Update local checkbox UI immediately.
     *  • Persist to DropdownDependenciesContext after the local state update
     *    calculation, not inside it.
     */
    latestDependenciesRef.current = updatedDependencies;
    setDependencies(updatedDependencies);

    debugLog('[DependencyManager] Saving dependencies for trigger option:', {
      optionsSetUFI,
      triggerOptionId,
      dependentOptionId,
      checked,
      updatedDependencies,
    });

    updateDependenciesForOption(
      optionsSetUFI,
      triggerOptionId,
      updatedDependencies
    );

    if (typeof markFormListsUnsavedWorkingCopy === 'function') {
      markFormListsUnsavedWorkingCopy({
        formId,
        source: 'dependencies',
        reason: 'dependency_update',
      });
    }
  }, [
    optionsSetUFI,
    triggerOptionId,
    updateDependenciesForOption,
    markFormListsUnsavedWorkingCopy,
    formId,
    debugLog,
  ]);

  // Display message if no optionId is provided
  if (!optionId) {
    return (
      <Box sx={{ p: 0, maxWidth: '85%', margin: '20px auto', textAlign: 'center' }}>
        <ErrorOutlineIcon sx={{ fontSize: '18px', color: 'rgba(0, 0, 0, 0.38)', mr: 1, verticalAlign: 'middle' }} />
        <Typography
          variant="body2"
          sx={{
            display: 'inline',
            color: 'rgba(0, 0, 0, 0.38)',
          }}
        >
          Please select an option from your dropdown list to set up dependencies.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ p: 0, maxWidth: '85%', margin: '0 auto' }}>
        {/* Zero-state message if only one field is available */}
        {hasInsufficientFields ? (
          <Typography
            variant="body2"
            align="center"
            sx={{
              mt: 2,
              color: 'rgba(0, 0, 0, 0.38)'
            }}
          >
            You need at least two dropdown fields to set up or view dependencies. 
          </Typography>
        ) : (
          <>
            {/* Display selected option label */}
            <Typography variant="body2" sx={{ ml: 0, mb: 2, mt: 0 }}>
              <span style={{ fontWeight: 'bold', color: '#003459' }}>Set dependency for:</span>
              <span style={{ fontWeight: 'light', color: 'text.secondary', marginLeft: '8px' }}>{optionLabel}</span>
            </Typography>

            {/* Dropdown Selector */}
            <Box className={styles.tableHeaderContainer} sx={{ mb: 0, pb: 0 }}>
              <FormControl fullWidth>
                <Select
                  value={dropdownFieldLabels.some(label => label.optionsSetUFI === selectedFieldUFI) ? selectedFieldUFI : ''}
                  onChange={handleSelectChange}
                  displayEmpty
                  variant="outlined"
                  sx={{
                    backgroundColor: '#f9f9f9',
                    padding: '3px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    marginBottom: 0,
                    paddingBottom: 1,
                    borderRadius: '0px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0',
                    },
                    '& .MuiSelect-select': {
                      padding: '8px',
                    },
                  }}
                  disabled={dependencies.length > 0} // Disable dropdown if dependencies are set
                >
                  <MenuItem value="" disabled sx={{ fontSize: '14px' }}>
                    Select a field to set dependencies
                  </MenuItem>
                  {dropdownFieldLabels.map(({ optionsSetUFI, label }) => (
                    <MenuItem key={optionsSetUFI} value={optionsSetUFI} sx={{ fontSize: '14px' }}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Table Header */}
            <Box className={styles.tableHeaderContainer} sx={{ mb: 0 }}>
              <Box
                className={`${styles.subHeader}`}
                sx={{ width: '60%', borderLeft: '1px solid #e0e0e0', borderRight: 'none', borderBottom: '1px solid #e0e0e0' }}
              >
                Options
              </Box>
              <Box
                className={`${styles.subHeader}`}
                sx={{ width: '40%', borderLeft: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}
              >
                Dependency
              </Box>
            </Box>

            {/* Table Body */}
            <Box className={styles.tableBody}>
              {availableOptions.map((option) => (
                <Box key={option.optionId} className={styles.tableRow}>
                  <Box
                    className={styles.tableCell}
                    sx={{ width: '60%', fontSize: '11px', textAlign: 'left', borderLeft: '1px solid #e0e0e0', borderRight: 'none' }}
                  >
                    {option.label}
                  </Box>
                  <Box
                    className={styles.tableCellDep}
                    sx={{ width: '40%', textAlign: 'center', borderLeft: '1px solid #e0e0e0' }}
                  >
                    <Checkbox
                      checked={dependencies.includes(option.optionId)}
                      onChange={(e) => handleCheckboxChange(option.optionId, e.target.checked)}
                      sx={{
                        p: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: '14px',
                          strokeWidth: '1',
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DependencyManager;
